import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { Card, Chip, Button, Divider } from 'react-native-paper';
import { useTheme } from '../contexts/ThemeContext';
import { CulturalContent } from '../services/ApiService';
import { ThemedContainer } from '../components/ThemedContainer';

interface CulturalContentDetailScreenProps {
  route: {
    params: {
      content: CulturalContent;
    };
  };
  navigation: any;
}

const CulturalContentDetailScreen: React.FC<CulturalContentDetailScreenProps> = ({ route, navigation }) => {
  const { content } = route.params;
  const { themeData } = useTheme();

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleExternalLink = (url: string) => {
    if (url) {
      Linking.openURL(url);
    }
  };

  return (
    <ThemedContainer>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBackPress} style={styles.backButton}>
            <Text style={[styles.backButtonText, { color: themeData.colors.highlight1 }]}>
              ← Back
            </Text>
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: themeData.colors.text }]}>
            Cultural Content
          </Text>
        </View>

        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
        >
          {/* Title Card */}
          <Card
            style={[
              styles.titleCard,
              {
                backgroundColor: themeData.colors.cardBackground,
                borderColor: themeData.colors.cardBorder,
              }
            ]}
          >
            <Card.Content>
              <Text style={[styles.title, { color: themeData.colors.text }]}>
                {content.title}
              </Text>
              
              <View style={styles.metaContainer}>
                <Chip
                  mode="outlined"
                  textStyle={{ color: themeData.colors.highlight1 }}
                  style={[styles.typeChip, { borderColor: themeData.colors.highlight1 }]}
                >
                  {content.content_type}
                </Chip>
                
                {content.category && (
                  <Chip
                    mode="outlined"
                    textStyle={{ color: themeData.colors.highlight2 }}
                    style={[styles.categoryChip, { borderColor: themeData.colors.highlight2 }]}
                  >
                    {content.category}
                  </Chip>
                )}
              </View>

              {content.author && (
                <Text style={[styles.author, { color: themeData.colors.textSecondary }]}>
                  by {content.author}
                </Text>
              )}

              {content.publish_date && (
                <Text style={[styles.date, { color: themeData.colors.textTertiary }]}>
                  Published: {new Date(content.publish_date).toLocaleDateString()}
                </Text>
              )}
            </Card.Content>
          </Card>

          {/* Content Card */}
          <Card
            style={[
              styles.contentCard,
              {
                backgroundColor: themeData.colors.cardBackground,
                borderColor: themeData.colors.cardBorder,
              }
            ]}
          >
            <Card.Content>
              <Text style={[styles.contentTitle, { color: themeData.colors.text }]}>
                Content
              </Text>
              <Divider style={[styles.divider, { backgroundColor: themeData.colors.cardBorder }]} />
              <Text style={[styles.content, { color: themeData.colors.text }]}>
                {content.content}
              </Text>
            </Card.Content>
          </Card>

          {/* Summary Card */}
          {content.summary && (
            <Card
              style={[
                styles.summaryCard,
                {
                  backgroundColor: themeData.colors.cardBackground,
                  borderColor: themeData.colors.cardBorder,
                }
              ]}
            >
              <Card.Content>
                <Text style={[styles.summaryTitle, { color: themeData.colors.text }]}>
                  Summary
                </Text>
                <Divider style={[styles.divider, { backgroundColor: themeData.colors.cardBorder }]} />
                <Text style={[styles.summary, { color: themeData.colors.textSecondary }]}>
                  {content.summary}
                </Text>
              </Card.Content>
            </Card>
          )}

          {/* Tags Card */}
          {content.tags && content.tags.length > 0 && (
            <Card
              style={[
                styles.tagsCard,
                {
                  backgroundColor: themeData.colors.cardBackground,
                  borderColor: themeData.colors.cardBorder,
                }
              ]}
            >
              <Card.Content>
                <Text style={[styles.tagsTitle, { color: themeData.colors.text }]}>
                  Tags
                </Text>
                <Divider style={[styles.divider, { backgroundColor: themeData.colors.cardBorder }]} />
                <View style={styles.tagsContainer}>
                  {content.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      mode="outlined"
                      compact
                      textStyle={{ color: themeData.colors.highlight3 }}
                      style={[
                        styles.tagChip,
                        { borderColor: themeData.colors.highlight3 }
                      ]}
                    >
                      {tag}
                    </Chip>
                  ))}
                </View>
              </Card.Content>
            </Card>
          )}

          {/* Links Card */}
          {(content.original_url || content.wayback_url) && (
            <Card
              style={[
                styles.linksCard,
                {
                  backgroundColor: themeData.colors.cardBackground,
                  borderColor: themeData.colors.cardBorder,
                }
              ]}
            >
              <Card.Content>
                <Text style={[styles.linksTitle, { color: themeData.colors.text }]}>
                  External Links
                </Text>
                <Divider style={[styles.divider, { backgroundColor: themeData.colors.cardBorder }]} />
                
                {content.original_url && (
                  <TouchableOpacity
                    onPress={() => handleExternalLink(content.original_url!)}
                    style={styles.linkButton}
                  >
                    <Text style={[styles.linkText, { color: themeData.colors.highlight1 }]}>
                      View Original Page
                    </Text>
                  </TouchableOpacity>
                )}
                
                {content.wayback_url && (
                  <TouchableOpacity
                    onPress={() => handleExternalLink(content.wayback_url!)}
                    style={styles.linkButton}
                  >
                    <Text style={[styles.linkText, { color: themeData.colors.highlight2 }]}>
                      View Archived Version
                    </Text>
                  </TouchableOpacity>
                )}
              </Card.Content>
            </Card>
          )}

          {/* Source Info */}
          <Card
            style={[
              styles.sourceCard,
              {
                backgroundColor: themeData.colors.cardBackground,
                borderColor: themeData.colors.cardBorder,
              }
            ]}
          >
            <Card.Content>
              <Text style={[styles.sourceTitle, { color: themeData.colors.text }]}>
                Source Information
              </Text>
              <Divider style={[styles.divider, { backgroundColor: themeData.colors.cardBorder }]} />
              <Text style={[styles.sourceText, { color: themeData.colors.textTertiary }]}>
                Scraped from Critiki archives
              </Text>
              {content.wayback_timestamp && (
                <Text style={[styles.sourceText, { color: themeData.colors.textTertiary }]}>
                  Archive date: {new Date(content.wayback_timestamp).toLocaleDateString()}
                </Text>
              )}
              <Text style={[styles.sourceText, { color: themeData.colors.textTertiary }]}>
                Added: {new Date(content.created_at).toLocaleDateString()}
              </Text>
            </Card.Content>
          </Card>
        </ScrollView>
      </View>
    </ThemedContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 20,
  },
  backButton: {
    marginRight: 16,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingTop: 0,
  },
  titleCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 12,
    lineHeight: 32,
  },
  metaContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  typeChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  categoryChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  author: {
    fontSize: 14,
    fontStyle: 'italic',
    marginBottom: 4,
  },
  date: {
    fontSize: 12,
  },
  contentCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  contentTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  divider: {
    marginBottom: 12,
  },
  content: {
    fontSize: 16,
    lineHeight: 24,
  },
  summaryCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  summary: {
    fontSize: 14,
    lineHeight: 20,
    fontStyle: 'italic',
  },
  tagsCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  tagsTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tagChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  linksCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  linksTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  linkButton: {
    paddingVertical: 8,
    marginBottom: 8,
  },
  linkText: {
    fontSize: 16,
    textDecorationLine: 'underline',
  },
  sourceCard: {
    marginBottom: 16,
    borderWidth: 1,
  },
  sourceTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  sourceText: {
    fontSize: 12,
    marginBottom: 4,
  },
});

export default CulturalContentDetailScreen;
